import { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Info, Dumbbell } from 'lucide-react';
import axios from 'axios';
import { ExerciseDemoModal } from './ExerciseDemoModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Cache the entire library at module level — it's small (~40 entries) and
// rarely changes during a session, so this keeps the workout page snappy.
let _libraryCache = null;
let _libraryPromise = null;
const fetchLibrary = () => {
  if (_libraryCache) return Promise.resolve(_libraryCache);
  if (_libraryPromise) return _libraryPromise;
  _libraryPromise = axios
    .get(`${BACKEND}/api/exercises`, { withCredentials: true })
    .then((res) => {
      _libraryCache = res.data?.exercises || [];
      return _libraryCache;
    })
    .catch(() => {
      _libraryCache = [];
      return _libraryCache;
    });
  return _libraryPromise;
};

export const ExerciseList = ({ exercises, currentExercise, currentSet }) => {
  const [library, setLibrary] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchLibrary().then((lib) => { if (alive) setLibrary(lib); });
    return () => { alive = false; };
  }, []);

  const byId = useMemo(() => {
    const m = new Map();
    library.forEach((e) => m.set(e.id, e));
    return m;
  }, [library]);

  // Build a normalised-name lookup for cases where the workout exercise hasn't
  // been linked yet (exercise_id is null) but the free-text name happens to
  // match a library entry exactly. Lets us still surface the demo modal.
  const byNorm = useMemo(() => {
    const m = new Map();
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    library.forEach((e) => {
      [e.name, ...(e.aliases || [])].forEach((alias) => {
        const n = norm(alias);
        if (n && !m.has(n)) m.set(n, e);
      });
    });
    return m;
  }, [library]);

  const resolveLibEntry = (ex) => {
    if (ex.exercise_id && byId.has(ex.exercise_id)) return byId.get(ex.exercise_id);
    const n = (ex.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (n && byNorm.has(n)) return byNorm.get(n);
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-3xl p-4 sm:p-6" data-testid="exercise-list">
      <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4 px-1">Exercises</h3>
      <div className="space-y-2.5 max-h-[28rem] overflow-y-auto pr-1 -mr-1">
        {exercises?.map((ex, i) => {
          const lib = resolveLibEntry(ex);
          const isCurrent = i === currentExercise;
          const isDone = i < currentExercise;
          const thumb = lib?.thumbnail_url || lib?.image_url;
          return (
            <div
              key={ex.id || i}
              className={`rounded-2xl border p-3 flex items-center gap-3 transition-colors ${
                isCurrent
                  ? 'border-primary/70 bg-primary/10 shadow-[0_0_24px_rgba(255,215,0,0.08)]'
                  : isDone
                    ? 'border-green-500/30 bg-green-500/[0.07]'
                    : 'border-white/10 bg-white/[0.02]'
              }`}
              data-testid={`exercise-row-${i}`}
            >
              <span className={`h-8 w-8 rounded-full flex items-center justify-center font-heading text-base shrink-0 ${
                isDone ? 'bg-green-500 text-white' : isCurrent ? 'bg-primary text-black' : 'bg-white/[0.06] text-white/50'
              }`}>
                {isDone ? <CheckCircle className="h-4 w-4" /> : i + 1}
              </span>
              {thumb ? (
                <img src={thumb} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />
              ) : lib ? (
                <div className="h-12 w-12 rounded-xl bg-white/[0.04] flex items-center justify-center shrink-0 text-white/40">
                  <Dumbbell className="h-4 w-4" />
                </div>
              ) : null}
              <div className="min-w-0 flex-1">
                {lib ? (
                  <button
                    type="button"
                    onClick={() => setActive(lib)}
                    className="max-w-full text-white font-medium hover:text-primary transition-colors flex items-center gap-1.5 text-left"
                    data-testid={`exercise-demo-trigger-${i}`}
                  >
                    <span className="truncate">{ex.name}</span>
                    <Info className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                  </button>
                ) : (
                  <p className="text-white font-medium truncate">{ex.name}</p>
                )}
                {ex.description && (
                  <p className="text-white/45 text-xs truncate mt-0.5">{ex.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px]">
                  {ex.reps && (
                    <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-white/70">{ex.reps} × {ex.sets} sets</span>
                  )}
                  {ex.duration_seconds > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-heading tracking-wider">{formatTime(ex.duration_seconds)}</span>
                  )}
                  {isCurrent && ex.sets > 1 && (
                    <span className="px-2 py-0.5 rounded-full bg-primary text-black font-semibold" data-testid="exercise-current-set">Set {currentSet}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {active && (
        <ExerciseDemoModal exercise={active} onClose={() => setActive(null)} />
      )}
    </div>
  );
};
